import { useEffect, useState } from "react";
import { Layout, CircleUserRound, File, RadioTower } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router-dom";

// Define the nav button variants using cva
const navButtonVariants = cva(
	"flex items-center space-x-3 w-full text-left px-3 py-2 border-2 border-black rounded-sm font-medium text-sm transition-colors",
	{
		variants: {
			variant: {
				default:
					"bg-[#37C5B3]/70 backdrop-blur-md text-white hover:bg-[#A56FF1]/40 hover:backdrop-blur-md shadow-[2px_2px_0_0_black]",
				selected:
					"bg-[#A56FF1]/80 backdrop-blur-md text-white hover:bg-[#A56FF1]/40 hover:backdrop-blur-md shadow-[2px_2px_0_0_black]",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	}
);


// Type for the nav item click handler
export type NavItemClickHandler = (id: string) => void;

// Interface for nav items
interface NavItem {
	id: string;
	label: string;
	icon: React.ReactNode;
}

// Props interface for the LeftNav component
export interface LeftNavProps extends VariantProps<typeof navButtonVariants> {
	/** Callback fired when a nav item is selected */
	onSelect?: NavItemClickHandler;
	/** Currently selected nav item ID */
	selectedId?: string;
	/** Default selected nav item ID (for uncontrolled usage) */
	defaultSelectedId?: string;
	/** Additional class names */
	className?: string;
	buttonStyleOverrides?: string;
}

const navItems: NavItem[] = [
	{
		id: "overview",
		label: "Overview",
		icon: <Layout className="h-5 w-5 text-white" />,
	},
	{
		id: "operators",
		label: "Operators",
		icon: <CircleUserRound className="h-5 w-5 text-white" />,
	},
	{
		id: "blobs",
		label: "Blobs",
		icon: <File className="h-5 w-5 text-white" />,
	},
	{
		id: "events",
		label: "Events",
		icon: <RadioTower className="h-5 w-5 text-white" />,
	},
];

export function LeftNav({
	onSelect,
	selectedId: controlledSelectedId,
	defaultSelectedId = "overview",
	className,
	buttonStyleOverrides,
}: LeftNavProps) {
	const location = useLocation();

	// Support both controlled and uncontrolled modes
	const [internalSelectedId, setInternalSelectedId] = useState<string>(defaultSelectedId);
	const selectedId = controlledSelectedId ?? internalSelectedId;

	const handleSelect = (id: string) => {
		if (!controlledSelectedId) {
			setInternalSelectedId(id);
		}
		onSelect?.(id);
	};

	useEffect(() => {
		setInternalSelectedId(location.pathname.replace("/", ""))
	}, [location.pathname])

	useEffect(() => {
		setInternalSelectedId(location.pathname.replace("/", ""))
	}, [])

	return (
		<div className={cn("flex flex-col space-y-3 w-64 px-3 py-4 border-2 bg-[#C0E6FF]/10 backdrop-blur-md border-black rounded-sm shadow-[4px_4px_0_0_black]", className)}>
			{navItems.map((item) => (
				<Button
					key={item.id}
					variant="ghost"
					className={cn(
						navButtonVariants({ variant: selectedId === item.id ? "selected" : "default" }),
						"justify-start",
						buttonStyleOverrides
					)}
					onClick={() => handleSelect(item.id)}
				>
					<span className="text-current">{item.icon}</span>
					<span className="text-sm">{item.label}</span>
				</Button>

			))}
		</div>
	);
}